import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useGLTF } from '@react-three/drei';
import type { ManifestObject } from '../content/schema';
import { onMeshClick } from '../selection/onMeshClick';

/**
 * Renders a single ManifestObject that carries a `model` path (i.e. was
 * authored as a .glb by a Phase 6 generator). Phase 3's PlaceholderObject
 * handles the legacy placeholder-geometry branch; this component handles
 * the .glb branch.
 *
 * Responsibilities:
 *   1. Load the .glb via drei's `useGLTF` (suspense-ready; caches across
 *      remounts). Asset paths land as e.g. `/models/1925/hero/pavilion.glb`.
 *   2. Apply the manifest object's position / rotation / scale to a group
 *      wrapping the loaded scene.
 *   3. Thread userData into every descendant mesh so raycast selection
 *      (onMeshClick) can resolve manifestId + sourceIds without a second
 *      manifest lookup (mirrors PlaceholderObject's contract).
 *   4. Attach onClick to the outer group for selection dispatch.
 *   5. Compute an AABB (Box3.setFromObject) after the scene mounts and
 *      report it upward via `onCollisionReady`. SceneGraph aggregates these
 *      into `useNavStore.collisionBoxes` alongside placeholder AABBs.
 *      Terrain + water are excluded — terrain is walkable ground, water
 *      is a traversable plane.
 */

/** Manifest IDs whose AABB must NOT block the walk controller. */
const NON_COLLIDABLE_GLB_IDS = new Set<string>([
  'terrain-lake-basin',
  'edgewood-lake-water',
]);

interface GltfObjectProps {
  obj: ManifestObject;
  /** Called once after the group mounts. Passes the manifestId + world AABB (or null if non-collidable). */
  onCollisionReady?: (id: string, aabb: { min: [number, number, number]; max: [number, number, number] } | null) => void;
}

export function GltfObject({ obj, onCollisionReady }: GltfObjectProps) {
  const groupRef = useRef<THREE.Group>(null);
  // useGLTF expects a URL string; manifest `model` is relative to public/.
  const path = `/${obj.model}`;
  const gltf = useGLTF(path);

  // Propagate manifest metadata into every descendant mesh's userData so
  // raycast selection can resolve it via event.object.userData.
  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    group.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        child.userData = {
          ...child.userData,
          manifestId: obj.id,
          confidence: obj.confidence,
          sourceIds: obj.sourceIds,
          era: obj.era,
        };
      }
    });
  }, [obj.id, obj.confidence, obj.sourceIds, obj.era, gltf.scene]);

  // After mount: compute world-space AABB (Box3.setFromObject walks the
  // whole subtree) and report upward. Skip terrain + water — they are the
  // walkable surface, not obstacles.
  useEffect(() => {
    const group = groupRef.current;
    if (!group || !onCollisionReady) return;
    if (NON_COLLIDABLE_GLB_IDS.has(obj.id)) {
      onCollisionReady(obj.id, null);
      return;
    }
    // Ensure world matrices are current before measuring.
    group.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(group);
    if (box.isEmpty()) {
      onCollisionReady(obj.id, null);
      return;
    }
    onCollisionReady(obj.id, {
      min: [box.min.x, box.min.y, box.min.z],
      max: [box.max.x, box.max.y, box.max.z],
    });
  }, [obj.id, onCollisionReady, gltf.scene]);

  return (
    <group
      ref={groupRef}
      position={obj.position}
      rotation={obj.rotation}
      scale={obj.scale}
      onClick={onMeshClick}
    >
      <primitive object={gltf.scene} />
    </group>
  );
}
